import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import { Reveal, Kicker } from "@/components/Reveal";
import { api } from "@/lib/api";

const selectCls =
  "min-h-[44px] w-full border border-[#D9DDE3] bg-white px-3.5 py-2.5 text-sm font-medium text-[#161616] focus:border-[#F5B700] focus:outline-none";

function SkeletonCard() {
  return (
    <div className="animate-pulse border border-[#D9DDE3] bg-white" data-testid="product-skeleton">
      <div className="aspect-[4/3] bg-[#e5e8ec]" />
      <div className="space-y-3 p-5">
        <div className="h-4 w-3/4 bg-[#e5e8ec]" />
        <div className="h-3 w-1/2 bg-[#e5e8ec]" />
        <div className="h-9 w-full bg-[#e5e8ec]" />
      </div>
    </div>
  );
}

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const ptype = params.get("ptype") || "";

  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    api.get("/products/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set("q", searchInput);
        else next.delete("q");
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, setParams]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { q, category, brand, ptype } })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [q, category, brand, ptype]);

  const setFilter = (key) => (e) => {
    const v = e.target.value;
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set(key, v);
      else next.delete(key);
      return next;
    });
  };

  const clearAll = () => {
    setSearchInput("");
    setParams({});
  };

  const hasFilters = q || category || brand || ptype;
  const activeCat = useMemo(() => meta?.categories?.find((c) => c.slug === category), [meta, category]);

  return (
    <>
      <SEO
        title="Product Catalogue | Hari Om Trading Co. — Tahliwal, Una"
        description="Browse industrial automation, switchgear, lighting, pneumatic and electrical products. Search by name, model number or brand and send an enquiry."
      />
      <section className="grain bg-[#111111] bg-grid-dark py-16 lg:py-20" data-testid="products-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <Kicker dark>Catalogue</Kicker>
            <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              {activeCat ? activeCat.name : "Product Catalogue"}
            </h1>
            <p className="mt-4 max-w-2xl text-base text-[#b8bec8]">
              Search by product name, model number or brand. If a price is not listed,
              send an enquiry for the latest quotation.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative mt-9 max-w-2xl">
              <Search size={19} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#667085]" aria-hidden="true" />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search product name, model number or brand…"
                aria-label="Search products"
                data-testid="product-search-input"
                className="min-h-[54px] w-full border border-[#3a3d42] bg-[#202124] pl-12 pr-4 text-[15px] text-white placeholder:text-[#667085] focus:border-[#F5B700] focus:outline-none"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F6F7F8] py-10 lg:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <button
            className="mb-5 flex min-h-[44px] items-center gap-2 border border-[#D9DDE3] bg-white px-4 py-2.5 text-sm font-semibold md:hidden"
            onClick={() => setFiltersOpen(!filtersOpen)}
            aria-expanded={filtersOpen}
            data-testid="filters-toggle"
          >
            <SlidersHorizontal size={16} aria-hidden="true" /> Filters {hasFilters ? "(active)" : ""}
          </button>

          <div className={`${filtersOpen ? "grid" : "hidden"} gap-3 md:grid md:grid-cols-4`} data-testid="product-filters">
            <select value={category} onChange={setFilter("category")} aria-label="Filter by category" data-testid="filter-category" className={selectCls}>
              <option value="">All Categories</option>
              {meta?.categories?.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
            <select value={brand} onChange={setFilter("brand")} aria-label="Filter by brand" data-testid="filter-brand" className={selectCls}>
              <option value="">All Brands</option>
              {meta?.brands?.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            <select value={ptype} onChange={setFilter("ptype")} aria-label="Filter by product type" data-testid="filter-type" className={selectCls}>
              <option value="">All Product Types</option>
              {meta?.types?.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {hasFilters && (
              <button onClick={clearAll} data-testid="filters-clear" className="flex min-h-[44px] items-center justify-center gap-2 border border-[#111111] px-4 py-2.5 text-sm font-semibold text-[#111111] hover:bg-[#111111] hover:text-white">
                <X size={15} aria-hidden="true" /> Clear filters
              </button>
            )}
          </div>

          <p className="mt-6 text-sm font-medium text-[#667085]" data-testid="products-count" aria-live="polite">
            {loading ? "Loading products…" : `${products.length} product${products.length === 1 ? "" : "s"} found`}
          </p>

          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="products-grid">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
              : products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>

          {!loading && products.length === 0 && (
            <div className="border border-[#D9DDE3] bg-white px-6 py-16 text-center" data-testid="products-no-results">
              <p className="font-heading text-2xl font-semibold text-[#161616]">No products matched your search.</p>
              <p className="mx-auto mt-3 max-w-md text-sm text-[#667085]">
                Product not found? Send us your requirement and we&rsquo;ll help you check the available range.
              </p>
              <Link
                to={`/quote${q ? `?product=${encodeURIComponent(q)}` : ""}`}
                data-testid="no-results-enquiry-cta"
                className="mt-7 inline-flex min-h-[48px] items-center bg-[#F5B700] px-7 py-3 font-heading text-[15px] font-semibold text-[#111111] hover:bg-[#D99C00]"
              >
                Send Product Enquiry
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
